import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/pages/DashboardPage.vue'),
        meta: { title: 'لوحة التحكم', icon: 'home', minRole: 'manager' },
      },
      {
        path: 'sales-dashboard',
        name: 'sales-dashboard',
        component: () => import('@/pages/DashboardPage.vue'),
        meta: { title: 'لوحة التحكم', icon: 'home', minRole: 'manager' },
      },
      {
        path: 'reps',
        name: 'reps',
        component: () => import('@/pages/reps/RepListPage.vue'),
        meta: { title: 'المناديب', icon: 'users' },
      },
      {
        path: 'reps/:code',
        name: 'rep-profile',
        component: () => import('@/pages/reps/RepProfilePage.vue'),
        meta: { title: 'ملف المندوب', parent: 'reps' },
        props: true,
      },
      {
        path: 'customers',
        name: 'customers',
        component: () => import('@/pages/customers/CustomerListPage.vue'),
        meta: { title: 'العملاء', icon: 'briefcase' },
      },
      {
        path: 'customers/:code',
        name: 'customer-profile',
        component: () => import('@/pages/customers/CustomerProfilePage.vue'),
        meta: { title: 'ملف العميل', parent: 'customers' },
        props: true,
      },
      // ── العملاء — customer breakdown screens (fed by ONYX imports) ──────
      // Wrapped in CustomerReportsLayout so a single مركز التكلفة filter is
      // shared across every subscreen. List screens opt into the filter via
      // meta.costCenterFilter; the scorecard and rule editor leave it off.
      {
        path: 'customer-reports',
        component: () => import('@/layouts/CustomerReportsLayout.vue'),
        children: [
          {
            path: '',
            name: 'customer-reports-hub',
            component: () => import('@/pages/customer-reports/CustomersHubPage.vue'),
            meta: { title: 'العملاء', icon: 'briefcase', costCenterFilter: true },
          },
          {
            path: 'classification-rep',
            name: 'customer-reports-classification-rep',
            component: () => import('@/pages/customer-reports/ClassificationByRepPage.vue'),
            meta: { title: 'تصنيف + موظف', parent: 'customer-reports-hub', costCenterFilter: true },
          },
          {
            path: 'rep-classification',
            name: 'customer-reports-rep-classification',
            component: () => import('@/pages/customer-reports/RepByClassificationPage.vue'),
            meta: { title: 'موظف + تصنيف', parent: 'customer-reports-hub', costCenterFilter: true },
          },
          {
            path: 'volume-general',
            name: 'customer-reports-volume-general',
            component: () => import('@/pages/customer-reports/VolumeGeneralPage.vue'),
            meta: { title: 'حجم + عام', parent: 'customer-reports-hub', costCenterFilter: true },
          },
          {
            path: 'rep-volume',
            name: 'customer-reports-rep-volume',
            component: () => import('@/pages/customer-reports/RepByVolumePage.vue'),
            meta: { title: 'موظف + حجم', parent: 'customer-reports-hub', costCenterFilter: true },
          },
          {
            path: 'line',
            name: 'customer-reports-line',
            component: () => import('@/pages/customer-reports/LinePage.vue'),
            meta: { title: 'خط', parent: 'customer-reports-hub', costCenterFilter: true },
          },
          {
            path: 'line-rep',
            name: 'customer-reports-line-rep',
            component: () => import('@/pages/customer-reports/LineByRepPage.vue'),
            meta: { title: 'خط + موظف', parent: 'customer-reports-hub', costCenterFilter: true },
          },
          {
            path: 'reps-line',
            name: 'customer-reports-reps-line',
            component: () => import('@/pages/customer-reports/RepsByLinePage.vue'),
            meta: { title: 'موظفون + خط', parent: 'customer-reports-hub', costCenterFilter: true },
          },
          {
            path: 'debts',
            name: 'customer-reports-debts',
            component: () => import('@/pages/customer-reports/DebtsPage.vue'),
            meta: { title: 'الديون', parent: 'customer-reports-hub', costCenterFilter: true },
          },
          {
            path: 'customer/:customerId',
            name: 'customer-reports-customer',
            component: () => import('@/pages/customer-reports/CustomerScorecardPage.vue'),
            meta: { title: 'بطاقة العميل', parent: 'customer-reports-hub' },
            props: true,
          },
          {
            path: 'rating-setup',
            name: 'customer-reports-rating-setup',
            component: () => import('@/pages/customer-reports/RatingSetupPage.vue'),
            meta: { title: 'قواعد التقييم', parent: 'customer-reports-hub', requiresAdmin: true },
          },
          {
            path: 'goals',
            name: 'customer-reports-goals',
            component: () => import('@/pages/customer-reports/CostCenterGoalsPage.vue'),
            meta: { title: 'أهداف مراكز التكلفة', parent: 'customer-reports-hub', requiresAdmin: true },
          },
        ],
      },
      {
        path: 'analytics',
        name: 'analytics',
        component: () => import('@/pages/AnalyticsPage.vue'),
        meta: { title: 'التحليلات', icon: 'chart', minRole: 'manager' },
      },
      {
        path: 'orders',
        name: 'orders',
        component: () => import('@/pages/orders/OrderListPage.vue'),
        meta: { title: 'الطلبات', icon: 'file-text' },
      },
      {
        path: 'orders/:id',
        name: 'order-detail',
        component: () => import('@/pages/orders/OrderDetailPage.vue'),
        meta: { title: 'تفاصيل الطلب', parent: 'orders' },
        props: true,
      },
      {
        path: 'admin/sync',
        name: 'sync',
        component: () => import('@/pages/SyncManagementPage.vue'),
        meta: { title: 'المزامنة', icon: 'refresh-cw' },
      },
      {
        path: 'admin/users',
        name: 'users',
        component: () => import('@/pages/admin/UsersManagementPage.vue'),
        meta: { title: 'إدارة المستخدمين', icon: 'shield', requiresAdmin: true },
      },
      {
        path: 'admin/roles',
        name: 'roles',
        component: () => import('@/pages/admin/RolesPermissionsPage.vue'),
        meta: { title: 'الأدوار والصلاحيات', icon: 'shield-check', requiresAdmin: true },
      },
      {
        path: 'admin/cost-center-admins',
        name: 'cost-center-admins',
        component: () => import('@/pages/admin/CostCenterAdminsPage.vue'),
        meta: { title: 'مدراء مراكز التكلفة', icon: 'shield', requiresAdmin: true },
      },
      {
        path: 'admin/rating-settings',
        name: 'rating-overview',
        component: () => import('@/pages/admin/rating/RatingOverviewPage.vue'),
        meta: { title: 'إعدادات التقييم', icon: 'settings', requiresAdmin: true },
      },
      {
        path: 'admin/rating-settings/departments',
        name: 'rating-departments',
        component: () => import('@/pages/admin/rating/DepartmentsPage.vue'),
        meta: { title: 'الأقسام', parent: 'rating-overview', requiresAdmin: true },
      },
      {
        path: 'admin/rating-settings/entity-types',
        name: 'rating-entity-types',
        component: () => import('@/pages/admin/rating/EntityTypesPage.vue'),
        meta: { title: 'أنواع الكيانات', parent: 'rating-overview', requiresAdmin: true },
      },
      {
        path: 'admin/rating-settings/matrix',
        name: 'rating-matrix',
        component: () => import('@/pages/admin/rating/DepartmentEntityMatrixPage.vue'),
        meta: { title: 'مصفوفة الأقسام/الكيانات', parent: 'rating-overview', requiresAdmin: true },
      },
      {
        path: 'admin/rating-settings/criteria',
        name: 'rating-criteria',
        component: () => import('@/pages/admin/rating/CriteriaPage.vue'),
        meta: { title: 'معايير التقييم', parent: 'rating-overview', requiresAdmin: true },
      },
      {
        path: 'admin/rating-settings/weights',
        name: 'rating-weights',
        component: () => import('@/pages/admin/rating/WeightsPage.vue'),
        meta: { title: 'أوزان المعايير', parent: 'rating-overview', requiresAdmin: true },
      },
      {
        path: 'admin/rating-settings/risk-rules',
        name: 'rating-risk-rules',
        component: () => import('@/pages/admin/rating/RiskRulesPage.vue'),
        meta: { title: 'قواعد المخاطر', parent: 'rating-overview', requiresAdmin: true },
      },
      {
        path: 'admin/rating-settings/classes',
        name: 'rating-classes',
        component: () => import('@/pages/admin/rating/RatingClassesPage.vue'),
        meta: { title: 'فئات التقييم', parent: 'rating-overview', requiresAdmin: true },
      },
      {
        path: 'admin/rating-settings/cost-center-types',
        name: 'rating-cost-center-types',
        component: () => import('@/pages/admin/rating/CostCenterTypesPage.vue'),
        meta: { title: 'أنواع مراكز التكلفة', parent: 'rating-overview', requiresAdmin: true },
      },
      {
        path: 'admin/rating-settings/expected-columns',
        name: 'rating-expected-columns',
        component: () => import('@/pages/admin/rating/ExcelColumnsPage.vue'),
        meta: { title: 'أعمدة Excel المتوقعة', parent: 'rating-overview', requiresAdmin: true },
      },
      // ── Phase 2: ONYX Excel Import ───────────────────────────────────
      {
        path: 'admin/rating-imports',
        name: 'rating-imports',
        component: () => import('@/pages/admin/rating-imports/RatingImportsPage.vue'),
        meta: { title: 'الاستيرادات', icon: 'upload', requiresAdmin: true },
      },
      {
        path: 'admin/rating-imports/new',
        name: 'rating-import-wizard',
        component: () => import('@/pages/admin/rating-imports/RatingImportWizardPage.vue'),
        meta: { title: 'استيراد جديد', parent: 'rating-imports', requiresAdmin: true },
      },
      {
        path: 'admin/rating-imports/:id',
        name: 'rating-import-detail',
        component: () => import('@/pages/admin/rating-imports/RatingImportBatchDetailPage.vue'),
        meta: { title: 'تفاصيل الاستيراد', parent: 'rating-imports', requiresAdmin: true },
        props: true,
      },
      // ── Phase 3: Rating Calculations ────────────────────────────────
      {
        path: 'admin/rating-calculations',
        name: 'rating-calculations',
        component: () => import('@/pages/admin/rating-calculations/RatingCalculationsPage.vue'),
        meta: { title: 'حسابات التقييم', icon: 'calculator', requiresAdmin: true },
      },
      {
        path: 'admin/rating-calculations/:id',
        name: 'rating-calculation-detail',
        component: () => import('@/pages/admin/rating-calculations/RatingCalculationDetailPage.vue'),
        meta: { title: 'تفاصيل حساب التقييم', parent: 'rating-calculations', requiresAdmin: true },
        props: true,
      },
      {
        path: 'admin/rating-dashboard',
        name: 'rating-dashboard-overview',
        component: () => import('@/pages/admin/rating-dashboard/RatingDashboardOverviewPage.vue'),
        meta: { title: 'لوحة تقييم الأداء', icon: 'bar-chart', requiresAdmin: true },
      },
      {
        path: 'admin/rating-dashboard/customers',
        name: 'rating-dashboard-customers',
        component: () => import('@/pages/admin/rating-dashboard/RatingDashboardCustomersPage.vue'),
        meta: { title: 'تقييم العملاء', parent: 'rating-dashboard-overview', requiresAdmin: true },
      },
      {
        path: 'admin/rating-dashboard/cost-centers',
        name: 'rating-dashboard-cost-centers',
        component: () => import('@/pages/admin/rating-dashboard/RatingDashboardCostCentersPage.vue'),
        meta: { title: 'تقييم مراكز التكلفة', parent: 'rating-dashboard-overview', requiresAdmin: true },
      },
      {
        path: 'admin/rating-dashboard/branches',
        name: 'rating-dashboard-branches',
        component: () => import('@/pages/admin/rating-dashboard/RatingDashboardBranchesPage.vue'),
        meta: { title: 'تقييم الفروع', parent: 'rating-dashboard-overview', requiresAdmin: true },
      },
      {
        path: 'admin/rating-dashboard/sales-reps',
        name: 'rating-dashboard-sales-reps',
        component: () => import('@/pages/admin/rating-dashboard/RatingDashboardSalesRepsPage.vue'),
        meta: { title: 'تقييم المناديب', parent: 'rating-dashboard-overview', requiresAdmin: true },
      },
      {
        path: 'admin/rating-dashboard/employees',
        name: 'rating-dashboard-employees',
        component: () => import('@/pages/admin/rating-dashboard/RatingDashboardEmployeesPage.vue'),
        meta: { title: 'تقييم الموظفين', parent: 'rating-dashboard-overview', requiresAdmin: true },
      },
      {
        path: 'admin/rating-dashboard/risks',
        name: 'rating-dashboard-risks',
        component: () => import('@/pages/admin/rating-dashboard/RatingDashboardRisksPage.vue'),
        meta: { title: 'مركز المخاطر', parent: 'rating-dashboard-overview', requiresAdmin: true },
      },
      {
        path: 'admin/rating-dashboard/debt-aging',
        name: 'rating-dashboard-debt-aging',
        component: () => import('@/pages/admin/rating-dashboard/RatingDashboardDebtAgingPage.vue'),
        meta: { title: 'تحليل أعمار الديون', parent: 'rating-dashboard-overview', requiresAdmin: true },
      },
      {
        path: 'admin/rating-dashboard/collections',
        name: 'rating-dashboard-collections',
        component: () => import('@/pages/admin/rating-dashboard/RatingDashboardCollectionsPage.vue'),
        meta: { title: 'تحليل التحصيل', parent: 'rating-dashboard-overview', requiresAdmin: true },
      },
      {
        path: 'admin/rating-dashboard/score/:scoreId',
        name: 'rating-dashboard-score-detail',
        component: () => import('@/pages/admin/rating-dashboard/RatingDashboardScoreDetailPage.vue'),
        meta: { title: 'تفاصيل التقييم', parent: 'rating-dashboard-overview', requiresAdmin: true },
        props: true,
      },
      {
        path: 'admin/rating-reports',
        name: 'rating-reports-center',
        component: () => import('@/pages/admin/rating/RatingReportsCenterPage.vue'),
        meta: { title: 'التقارير التنفيذية', icon: 'file-text', requiresAdmin: true },
      },
      {
        path: 'admin/rating-reports/score/:scoreId',
        name: 'rating-reports-score',
        component: () => import('@/pages/admin/rating/RatingReportBuilderPage.vue'),
        meta: { title: 'تقرير تفاصيل التقييم', parent: 'rating-reports-center', requiresAdmin: true },
        props: true,
      },
      {
        path: 'admin/rating-reports/:type',
        name: 'rating-reports-builder',
        component: () => import('@/pages/admin/rating/RatingReportBuilderPage.vue'),
        meta: { title: 'منشئ التقارير', parent: 'rating-reports-center', requiresAdmin: true },
        props: true,
      },
    ],
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/LoginPage.vue'),
    meta: { title: 'تسجيل الدخول', public: true },
  },
  {
    path: '/change-password',
    name: 'change-password',
    component: () => import('@/pages/ChangePasswordPage.vue'),
    meta: { title: 'تغيير كلمة المرور' },
  },
  {
    path: '/portal',
    component: () => import('@/layouts/PortalLayout.vue'),
    meta: { requiresSalesRep: true },
    children: [
      { path: '',                       name: 'portal-home',           component: () => import('@/pages/portal/PortalHomePage.vue'),            meta: { title: 'الرئيسية', icon: 'home' } },
      { path: 'customers',              name: 'portal-customers',      component: () => import('@/pages/portal/PortalCustomersPage.vue'),       meta: { title: 'العملاء', icon: 'users' } },
      { path: 'customers/:code',        name: 'portal-customer-detail',component: () => import('@/pages/portal/PortalCustomerDetailPage.vue'),  meta: { title: 'ملف العميل', parent: 'portal-customers' }, props: true },
      { path: 'ratings',                name: 'portal-ratings',        component: () => import('@/pages/portal/PortalCustomerRatingsPage.vue'), meta: { title: 'تقييم العملاء', icon: 'star' } },
      { path: 'orders',                 name: 'portal-orders',         component: () => import('@/pages/portal/PortalOrdersPage.vue'),          meta: { title: 'العمليات', icon: 'file-text' } },
      { path: 'debt-aging',             name: 'portal-debt-aging',     component: () => import('@/pages/portal/PortalDebtAgingPage.vue'),       meta: { title: 'أعمار الديون', icon: 'clock' } },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/NotFoundPage.vue'),
    meta: { title: 'غير موجود', public: true },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    return savedPosition ?? { top: 0 }
  },
})

/**
 * Where each role lands when they hit "/" or finish logging in. The العملاء hub
 * (first sidebar section) is the default landing for everyone except sales reps,
 * who are confined to their portal.
 */
function landingRouteFor(role: string): string {
  if (role === 'sales_rep') return 'portal-home'
  return 'customer-reports-hub'
}

router.beforeEach((to, _from, next) => {
  document.title = `${to.meta.title ?? 'الرئيسية'} | Sales Analytics`

  const auth = useAuthStore()

  // If route is public (login), allow access
  if (to.meta.public) {
    if (auth.isAuthenticated && to.name === 'login') {
      return next({ name: landingRouteFor(auth.userRole) })
    }
    return next()
  }

  // Protected route — require auth
  if (!auth.isAuthenticated) {
    return next({ name: 'login', query: { redirect: to.fullPath } })
  }

  // Force first-login password change — only the change-password page is allowed
  if (auth.mustChangePassword && to.name !== 'change-password') {
    return next({ name: 'change-password' })
  }

  // Sales reps may only access the portal namespace
  if (auth.isSalesRep && !String(to.path).startsWith('/portal') && to.name !== 'change-password') {
    return next({ name: 'portal-home' })
  }

  // Portal routes require sales-rep role
  if (to.meta.requiresSalesRep && !auth.isSalesRep) {
    return next({ name: landingRouteFor(auth.userRole) })
  }

  // Hitting "/" (the legacy sales dashboard) sends everyone but sales reps to the العملاء hub
  if (to.name === 'dashboard' && auth.userRole !== 'sales_rep') {
    return next({ name: 'customer-reports-hub' })
  }

  // Admin-only routes
  if (to.meta.requiresAdmin && auth.userRole !== 'admin') {
    return next({ name: landingRouteFor(auth.userRole) })
  }

  // Manager+ routes (block viewers and sales reps)
  if (to.meta.minRole === 'manager' && (auth.userRole === 'viewer' || auth.userRole === 'sales_rep')) {
    return next({ name: landingRouteFor(auth.userRole) })
  }

  next()
})

export default router
